import { Box, Divider, Typography } from '@mui/material';
import isEqual from 'lodash.isequal';
import { useMemo, useState } from 'react';
import { useAppSelector } from '@/shared/model';
import {
  Button,
  SearchInput,
  ColumnsList,
  ToggleButtonGroup,
} from '@/shared/ui';
import { selectUserSettingsMeta } from '../../model/slice';
import type { CLSFilters } from '../../model/types';
import { CLSType } from '../../model/types';

interface Props {
  value: CLSFilters;
  onChange: (value: CLSFilters) => void;
}

const CLS_TYPE_OPTIONS = [
  {
    label: 'Blacklist',
    value: CLSType.BLACKLIST,
    tooltip: 'User can access all columns except the unselected ones',
  },
  {
    label: 'Whitelist',
    value: CLSType.WHITELIST,
    tooltip: 'User can access only the selected columns',
  },
];

export function CLSPanel({ value, onChange }: Props) {
  const userSettingsMeta = useAppSelector(selectUserSettingsMeta);
  const [searchQuery, setSearchQuery] = useState('');
  const [internalValue, setInternalValue] = useState<CLSFilters>(value);
  const hasUnsavedChanges = useMemo(() => {
    return (
      value.type !== internalValue.type ||
      !isEqual(new Set(value.columns), new Set(internalValue.columns))
    );
  }, [value, internalValue]);
  const filteredColumns = useMemo(() => {
    if (!userSettingsMeta) {
      return [];
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();

    return userSettingsMeta?.clsColumns
      .filter((column) => {
        const config = userSettingsMeta.config[column];

        return !!config && config.name.toLowerCase().includes(normalizedQuery);
      })
      .map((column) => {
        const { name, description, group, order } =
          userSettingsMeta.config[column];

        return {
          name,
          description,
          value: column,
          group,
          order,
        };
      });
  }, [userSettingsMeta, searchQuery]);
  const selectedColumns = useMemo(() => {
    if (!userSettingsMeta) {
      return [];
    }

    const columnSet = new Set(internalValue.columns);

    if (internalValue.type === CLSType.BLACKLIST) {
      return userSettingsMeta.clsColumns.filter((col) => !columnSet.has(col));
    } else {
      return userSettingsMeta.clsColumns.filter((col) => columnSet.has(col));
    }
  }, [internalValue, userSettingsMeta]);

  const handleTypeChange = (newType: CLSType) => {
    const oldSet = new Set(internalValue.columns);
    const invertedColumns =
      userSettingsMeta?.clsColumns.filter((col) => !oldSet.has(col)) ?? [];

    setInternalValue({ type: newType, columns: invertedColumns });
  };
  const handleSelectedColumnsChange = (newCheckedColumns: string[]) => {
    const checkedSet = new Set(newCheckedColumns);

    const newColumns =
      internalValue.type === CLSType.BLACKLIST
        ? userSettingsMeta?.clsColumns.filter((col) => !checkedSet.has(col)) ??
          []
        : newCheckedColumns;

    setInternalValue({ ...internalValue, columns: newColumns });
  };
  const handleClearAll = () => {
    const clearedColumns =
      internalValue.type === CLSType.BLACKLIST
        ? userSettingsMeta?.clsColumns ?? []
        : [];

    setInternalValue({ ...internalValue, columns: clearedColumns });
  };
  const handleSubmitChange = () => {
    onChange(internalValue);
  };
  const handleResetChange = () => {
    setInternalValue(value);
  };

  return (
    <Box
      component="ul"
      sx={{
        height: '100%',
        overflow: 'hidden',
        m: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Divider />
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          fullWidth
          options={CLS_TYPE_OPTIONS}
          value={internalValue.type}
          onChange={handleTypeChange}
        />
        <SearchInput query={searchQuery} onChange={setSearchQuery} />
      </Box>
      <Divider />
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          component="span"
          variant="modalContent"
          sx={{ fontWeight: 500, color: '#7E828F' }}
        >
          Selected:{' '}
          <Box
            component="span"
            sx={{ color: (theme) => theme.palette.primary.main }}
          >
            {selectedColumns.length}
          </Box>{' '}
          of {userSettingsMeta?.clsColumns.length}
        </Typography>
        <Button
          sx={{ py: 0, lineHeight: '1.375rem', color: 'black' }}
          variant="text"
          color="secondary"
          onClick={handleClearAll}
        >
          Clear all
        </Button>
      </Box>
      <Divider />
      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        <ColumnsList
          columns={filteredColumns}
          selectedColumns={selectedColumns}
          onSelectedColumnsChange={handleSelectedColumnsChange}
        />
      </Box>
      {hasUnsavedChanges && (
        <Box
          sx={{
            borderTop: '1px solid #DDDFE7',
            backgroundColor: 'white',
            p: 2,
            display: 'flex',
            gap: 1,
          }}
        >
          <Button onClick={handleSubmitChange}>Apply</Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetChange}
          >
            Reset
          </Button>
        </Box>
      )}
    </Box>
  );
}
